import React, { Component } from 'react';
import moment from 'moment';
import { Redirect } from 'react-router-dom';

import { SCREEN_ID_UTILITY_SETUP_PROVIDER } from 'client/constants/screenIds';

import {
  SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_SELECTED_PROVIDER,
} from 'api/utilityActionTypes';

import { hasRoleAnonymous } from 'common/utility/userRoles';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import branch from 'client/core/branch';
import UtilityPartnerActions from 'client/actions/UtilityPartner';
import UtilityActivationActions from 'client/actions/UtilityActivation';

import SrUtilitySetupPage from 'client/components/SrUtilitySetup/SrUtilitySetupPage';
import SrUtilitySetupProvider from 'client/components/SrUtilitySetup/SrUtilitySetupProvider';
import SrUtilitySetupLazySignup from 'client/components/SrUtilitySetup/SrUtilitySetupLazySignup';
import SrUtilitySetupSetInternetModal from 'client/components/SrUtilitySetup/SrUtilitySetupSetInternetModal/SrUtilitySetupSetInternetModal';

@withTransitionHook
@withScreenId(SCREEN_ID_UTILITY_SETUP_PROVIDER)
@withScrollToTop
@branch({
  appUrl: ['env', 'APP_URL'],
})
@branch(({ match }) => ({
  utilityUser: ['utilityUser'],
  utilityActivation: ['utilityActivations', 'byShortId', match.params.utilityActivationShortId],
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
  userAgent: ['analytics', 'userAgent'],
}))
class UtilitySetupProviderPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Utility Setup | Provider',
  });

  static fetchData(match, inject) {
    return Promise.all([
      UtilityPartnerActions.getBySlug(inject, {
        slug: match.params.partnerSlug,
      }),
      UtilityActivationActions.getByShortId(inject, {
        utilityActivationShortId: match.params.utilityActivationShortId,
      }),
    ]);
  }

  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      callbackValues: null,
      callbackFunctionName: null,
    };
  }

  _onSubmit = (formValues) => {
    const { dispatch, match, history, utilityActivation, utilityUser } = this.props;

    if (hasRoleAnonymous(utilityUser.roles)) {
      this.setState({
        callbackValues: formValues,
        callbackFunctionName: '_onSubmit',
        setupInternetModalArgs: null,
      });
      return;
    }

    const { partnerSlug, utilityActivationShortId } = match.params;

    this.setState({
      isLoading: true,
    });

    const utilityTypeLabel = match.params.utilityTypeLabel;
    const utilityActivationItem = utilityActivation.items.find(
      (item) => item.type.label.toLowerCase() === utilityTypeLabel,
    );

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_SELECTED_PROVIDER,
      payload: {
        utilityActivationItemId: utilityActivationItem.id,
        utilityTypeId: utilityActivationItem.type.id,
        addressId: utilityActivation.addressId,
        utilityProviderId: formValues.otherProviderName ? null : formValues.utilityProviderId,
        accountNumber: formValues.accountNumber,
        activationDate: formValues.activationDate
          ? moment(formValues.activationDate).format('YYYY-MM-DD')
          : null,
        proofOfPurchaseUrl: formValues.proofOfPurchaseUrl,
        otherProviderName: formValues.otherProviderName || null,
      },
      onSuccess: (utilityActivation) => {
        dispatch(UtilityActivationActions.getByShortId, {
          utilityActivationShortId: utilityActivation.shortId,
          forceRefresh: true,
        })
          .then(() => {
            return dispatch(UtilityActivationActions.getByUserId, {
              utilityUserId: utilityActivation.utilityUserId,
              forceRefresh: true,
            });
          })
          .then(() => {
            history.push(`/utility-setup/${partnerSlug}/${utilityActivationShortId}`);
          });
      },
    });
  };

  _onHideUtility = () => {
    const { dispatch, match, history, utilityActivation, utilityUser } = this.props;

    if (hasRoleAnonymous(utilityUser.roles)) {
      this.setState({
        callbackFunctionName: '_onHideUtility',
      });

      return;
    }

    const { partnerSlug, utilityActivationShortId } = match.params;

    const utilityTypeLabel = match.params.utilityTypeLabel;
    const utilityActivationItem = utilityActivation.items.find(
      (item) => item.type.label.toLowerCase() === utilityTypeLabel,
    );

    const newUtilityTypesIds = utilityActivation.items
      .filter((item) => item.id !== utilityActivationItem.id)
      .map((item) => item.type.id);

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
      payload: {
        addressId: utilityActivation.addressId,
        utilityTypeIds: newUtilityTypesIds,
      },
      onSuccess: () => {
        dispatch(UtilityActivationActions.getByShortId, {
          utilityActivationShortId: utilityActivation.shortId,
          forceRefresh: true,
        }).then(() => {
          history.push(`/utility-setup/${partnerSlug}/${utilityActivationShortId}`);
        });
      },
      onError(error) {
        global.alert(error.message);
      },
    });
  };

  _onDownloadRequiredDocs = () => {
    const { utilityActivation, appUrl } = this.props;
    const stringQuery = JSON.stringify({
      addressId: utilityActivation.addressId,
      utilityPartnerId: utilityActivation.utilityPartnerId,
      addressDisplay: utilityActivation.address?.display,
    });
    const encodedQuery = encodeURIComponent(stringQuery);
    const downloadDocumentsUrl = `${appUrl}/bundle-files/utility-profit/required-utility-docs?data=${encodedQuery}`;

    global.open(downloadDocumentsUrl);
  };

  render() {
    const { utilityPartner, utilityActivation, userAgent, match } = this.props;

    const { isLoading, setupInternetModalArgs, callbackFunctionName, callbackValues } = this.state;

    const { partnerSlug, utilityActivationShortId } = match.params;

    const utilityTypeLabel = match.params.utilityTypeLabel;

    if (!utilityPartner || !utilityActivation) {
      return null;
    }

    const utilityActivationItem = utilityActivation.items.find(
      (item) => item.type.label.toLowerCase() === utilityTypeLabel,
    );

    if (!utilityActivationItem) {
      return <Redirect to={`/utility-setup/${partnerSlug}/${utilityActivationShortId}`} />;
    }

    const requiredDocs = utilityActivation.requiredDocs?.filter((doc) => {
      return doc.requiredByUtilityTypeIds?.includes(utilityActivationItem.type.id);
    });

    return (
      <SrUtilitySetupPage
        addressDisplay={utilityActivation.address?.display}
        utilityPartner={utilityPartner}
        utilityActivationId={utilityActivation.id}
        logoUrl={utilityPartner.logoUrl}
        backButtonText="Back"
        backButtonUrl={`/utility-setup/${utilityPartner.slug}/${utilityActivation.shortId}`}
      >
        <SrUtilitySetupProvider
          addressId={utilityActivation.addressId}
          partnerName={utilityPartner.name}
          partnerGroupId={utilityPartner.utilityPartnerGroupId}
          utilityActivation={utilityActivation}
          utilityActivationItem={utilityActivationItem}
          utilityActivationShortId={utilityActivation.shortId}
          userAgent={userAgent}
          requiredDocs={requiredDocs || []}
          isLoading={isLoading}
          onSubmit={this._onSubmit}
          onHideUtility={this._onHideUtility}
          onDownloadRequiredDocs={this._onDownloadRequiredDocs}
        />
        {callbackFunctionName && (
          <SrUtilitySetupLazySignup
            utilityActivation={utilityActivation}
            callbackFunction={() => {
              this[callbackFunctionName](callbackValues);
            }}
            onClose={() => this.setState({ callbackFunctionName: null, callbackValues: null })}
          />
        )}
        {setupInternetModalArgs && <SrUtilitySetupSetInternetModal {...setupInternetModalArgs} />}
      </SrUtilitySetupPage>
    );
  }
}

UtilitySetupProviderPage.displayName = 'UtilitySetupProviderPage';

export default UtilitySetupProviderPage;
